/* @jsx h */
import { defineComponent, h } from "vue";
import type { TableStatus } from "@lemmonhq/types";
import { formatPrice } from "@lemmonhq/orders";
import { cn, useLocalization } from "@lemmonhq/uikit";
import { tableAmountSummary } from "./tableCredit";

export type TableAmountLinesProps = {
  tableStatus: TableStatus;
  /** Needed unless `compact`. */
  currency?: string;
  /** One row of whole numbers, for the floor plan cells. */
  compact?: boolean;
  /** Horizontal alignment of the stacked lines (default "end"). */
  align?: "start" | "center" | "end";
};

/**
 * A table's amounts in the table views (list, details sheet, floor plan).
 * With table credit: credit loaded (green), order total (accent) and what is
 * left of the credit (red). Without: the order total only.
 */
export const TableAmountLines = defineComponent<TableAmountLinesProps>({
  name: "TableAmountLines",
  props: ["tableStatus", "currency", "compact", "align"],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      const { total, credit } = tableAmountSummary(props.tableStatus);
      const money = (value: number) =>
        props.compact
          ? value.toFixed(0)
          : formatPrice(value, true, 2, props.currency || "CHF");

      if (props.compact) {
        return (
          <span class="flex items-center gap-1 font-semibold tabular-nums">
            {credit ? (
              <span class="text-success" title={t("pos.table_credit.title")}>
                {money(credit.loaded)}
              </span>
            ) : null}
            {credit || total > 0 ? <span>{money(total)}</span> : null}
            {credit ? (
              <span class="text-error">{money(credit.left)}</span>
            ) : null}
          </span>
        );
      }

      return (
        <span
          class={cn(
            "flex flex-col tabular-nums leading-tight",
            props.align === "start"
              ? "items-start"
              : props.align === "center"
                ? "items-center"
                : "items-end",
          )}
        >
          {credit ? (
            <span
              class="flex items-center gap-0.5 font-semibold text-success"
              title={t("pos.table_credit.title")}
            >
              <span class="material-symbols-outlined" style="font-size: 1.1em;">
                account_balance_wallet
              </span>
              {money(credit.loaded)}
            </span>
          ) : null}
          <span class="text-accent">{money(total)}</span>
          {credit ? (
            <span class="font-semibold text-error">{money(credit.left)}</span>
          ) : null}
        </span>
      );
    };
  },
});
